/**
 * File: navActiveUtils.ts
 * Path: apps/admin/src/modules/layout/utils/navActiveUtils.ts
 * Description: Which menu links count as the page being shown, for the sidebar and the mobile bar.
 */

import { ADMIN_PATHS, OPEN_LEAD_STAGES, type AdminNavItem, type LeadStage } from "@idk/config";

/** True when the page being shown is this link or one of its sub-pages. */
export const isCurrent = (pathname: string, href: string | undefined): boolean =>
  !!href && (pathname === href || pathname.startsWith(`${href}/`));

/** The bottom bar's button for a group is lit on its own page and on any page inside it. */
export const groupIsActive = (group: AdminNavItem, pathname: string): boolean =>
  isCurrent(pathname, group.hub) || (group.children ?? []).some((child) => isCurrent(pathname, child.href));

/** The id in /businesses/<id> (or a sub-page of it), else null. */
export const businessIdFromPath = (pathname: string): string | null =>
  /^\/businesses\/([^/]+)/.exec(pathname)?.[1] ?? null;

/**
 * The menu link a business page belongs to: Pipeline while it is an open lead,
 * Clients once won, past, or a client that never was a lead (`stage` null).
 * Null for any other page, and while the stage is still loading (`undefined`).
 */
export const businessNavHref = (pathname: string, stage: LeadStage | null | undefined): string | null => {
  if (!businessIdFromPath(pathname) || stage === undefined) return null;
  return stage !== null && OPEN_LEAD_STAGES.includes(stage) ? ADMIN_PATHS.leads.root : ADMIN_PATHS.clients.root;
};

/** True when this link is the page being shown, counting the business page under Pipeline or Clients. */
export const isNavCurrent = (pathname: string, href: string | undefined, stage: LeadStage | null | undefined): boolean =>
  isCurrent(pathname, href) || (!!href && businessNavHref(pathname, stage) === href);
